import React, { useMemo, useState } from 'react';
import { useAITTheme, type AITTheme } from '@this/shared/ui/theme';
import { useTravelRules } from '../hooks/useTravelRules';
import type { TravelRules } from '../types/travelRules';

const RulesPanel: React.FC = () => {
  const { theme } = useAITTheme();
  const [isOpen, setIsOpen] = useState(false);
  const { travelRules, loading } = useTravelRules();
  const styles = useMemo(() => getStyles(theme, isOpen), [theme, isOpen]);

  const handleToggle = () => setIsOpen(!isOpen);
  const handleClose = () => setIsOpen(false);

  return (
    <>
      <button type="button" style={styles.toggleButton} onClick={handleToggle}>
        📋 出張ルール
      </button>
      {isOpen && (
        <div
          role="button"
          tabIndex={0}
          aria-label="パネルを閉じる"
          style={styles.overlay}
          onClick={handleClose}
          onKeyDown={e => e.key === 'Escape' && handleClose()}
        />
      )}
      <div style={styles.container}>
        <div style={styles.header}>
          <h3 style={styles.headerTitle}>会社の出張ルール</h3>
          <button type="button" style={styles.closeButton} onClick={handleClose}>
            ✕
          </button>
        </div>
        <div style={styles.content}>
          {loading ? (
            <p style={styles.loadingText}>読み込み中...</p>
          ) : travelRules ? (
            <RulesContent rules={travelRules} styles={styles} />
          ) : (
            <p style={styles.errorText}>ルール情報を取得できませんでした</p>
          )}
        </div>
      </div>
    </>
  );
};

interface RulesContentProps {
  rules: TravelRules;
  styles: ReturnType<typeof getStyles>;
}

const RulesContent: React.FC<RulesContentProps> = ({ rules, styles }) => {
  const approvalText = formatApprovalRules(rules.approvalRules);
  const hotelText = formatHotelRules(rules.hotelRules);
  const flightText = formatFlightRules(rules.flightRules);
  const shinkansenText = formatShinkansenRules(rules.shinkansenRules);
  const deadlineText = formatDeadlineRules(rules.deadlineRules);
  const arrangementText = formatArrangementRules(rules.arrangementRequestRules);

  return (
    <>
      <h4 style={styles.sectionTitle}>承認・申請</h4>
      <ul style={styles.list}>
        <li>{approvalText}</li>
      </ul>

      <h4 style={styles.sectionTitle}>宿泊規程</h4>
      <ul style={styles.list}>
        {hotelText.map(text => (
          <li key={text}>{text}</li>
        ))}
      </ul>

      <h4 style={styles.sectionTitle}>航空券規程</h4>
      <ul style={styles.list}>
        {flightText.map(text => (
          <li key={text}>{text}</li>
        ))}
      </ul>

      <h4 style={styles.sectionTitle}>新幹線・特急</h4>
      <ul style={styles.list}>
        {shinkansenText.map(text => (
          <li key={text}>{text}</li>
        ))}
      </ul>

      <h4 style={styles.sectionTitle}>予約期限</h4>
      <ul style={styles.list}>
        {deadlineText.map(text => (
          <li key={text}>{text}</li>
        ))}
      </ul>

      {arrangementText && (
        <>
          <h4 style={styles.sectionTitle}>コンシェルジュ手配</h4>
          <ul style={styles.list}>
            <li>{arrangementText}</li>
          </ul>
        </>
      )}
    </>
  );
};

const formatApprovalRules = (rules: TravelRules['approvalRules']): string => {
  const domestic = rules.domesticApprovalRequired;
  const foreign = rules.foreignApprovalRequired;

  if (domestic && foreign) return '事前申請: 国内/海外ともに必要';
  if (domestic) return '事前申請: 国内のみ必要';
  if (foreign) return '事前申請: 海外のみ必要';
  return '事前申請: 不要';
};

const formatHotelRules = (rules: TravelRules['hotelRules']): string[] => {
  const result: string[] = [];

  const domesticLimit = rules.domesticPriceLimit
    ? `国内: ${rules.domesticPriceLimit.toLocaleString()}円/泊`
    : '国内: 上限なし';
  const foreignLimit = rules.foreignPriceLimit
    ? `海外: ${rules.foreignPriceLimit.toLocaleString()}円/泊`
    : '海外: 上限なし';
  result.push(`上限金額 - ${domesticLimit}、${foreignLimit}`);

  if (rules.showOverLimit || rules.showForeignOverLimit) {
    const parts: string[] = [];
    if (rules.showOverLimit) parts.push('国内');
    if (rules.showForeignOverLimit) parts.push('海外');
    result.push(`規程超過表示: ${parts.join('/')}あり`);
  }

  const breakfastParts: string[] = [];
  if (rules.allowBreakfastDomestic) breakfastParts.push('国内');
  if (rules.allowBreakfastForeign) breakfastParts.push('海外');
  if (breakfastParts.length > 0) {
    result.push(`朝食付きプラン: ${breakfastParts.join('/')}で利用可`);
  } else {
    result.push('朝食付きプラン: 利用不可');
  }

  return result;
};

const SEAT_CLASS_LABELS: Record<string, string> = {
  M: 'エコノミー',
  W: 'プレミアムエコノミー',
  C: 'ビジネス',
  F: 'ファースト'
};

const formatFlightRules = (rules: TravelRules['flightRules']): string[] => {
  const result: string[] = [];

  if (rules.availableSeatClasses.length > 0) {
    const classes = rules.availableSeatClasses.map(c => SEAT_CLASS_LABELS[c] || c).join('、');
    result.push(`利用可能クラス: ${classes}`);
  } else {
    result.push('利用可能クラス: 設定なし');
  }

  result.push(`座席アップグレード: ${rules.allowSeatUpgrade ? '可' : '不可'}`);

  return result;
};

const SEAT_TYPE_LABELS: Record<string, string> = {
  free: '自由席',
  reserve: '指定席',
  green: 'グリーン車'
};

const formatShinkansenRules = (rules: TravelRules['shinkansenRules']): string[] => {
  const result: string[] = [];

  if (rules.availableSeatTypes.length > 0) {
    const types = rules.availableSeatTypes.map(t => SEAT_TYPE_LABELS[t] || t).join('、');
    result.push(`座席タイプ: ${types}`);
  } else {
    result.push('座席タイプ: 設定なし');
  }

  const options: string[] = [];
  if (rules.showEx) options.push('EX-IC');
  if (rules.useQr) options.push('QRチケット');
  if (options.length > 0) {
    result.push(`利用可能オプション: ${options.join('、')}`);
  }

  return result;
};

const formatDeadlineRules = (rules: TravelRules['deadlineRules']): string[] => {
  return [
    `ホテル: ${rules.hotel.text}`,
    `国内航空券: ${rules.domesticAir.text}`,
    `新幹線: ${rules.shinkansen.text}`
  ];
};

const formatArrangementRules = (rules: TravelRules['arrangementRequestRules']): string | null => {
  if (!rules.enabled) return null;
  const fee = rules.fee ? `${rules.fee.toLocaleString()}円/件` : '無料';
  return `利用可能（手数料: ${fee}）`;
};

const getStyles = (theme: AITTheme, isOpen: boolean) => ({
  toggleButton: {
    position: 'fixed' as const,
    right: '20px',
    bottom: '200px',
    backgroundColor: theme.linkColor,
    color: '#ffffff',
    border: 'none',
    padding: '12px 16px',
    borderRadius: '24px',
    fontSize: '13px',
    fontWeight: 600 as const,
    cursor: 'pointer',
    boxShadow: '0 4px 12px rgba(0, 128, 203, 0.3)',
    zIndex: 900,
    display: 'flex' as const,
    alignItems: 'center' as const,
    gap: '6px'
  },
  overlay: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    zIndex: 999
  },
  container: {
    position: 'fixed' as const,
    right: isOpen ? 0 : -280,
    top: 0,
    width: '280px',
    height: '100vh',
    backgroundColor: '#fcfcfc',
    borderLeft: `1px solid ${theme.grayBorderColor}`,
    overflowY: 'auto' as const,
    zIndex: 1000,
    transition: 'right 0.3s ease-in-out',
    boxShadow: '-2px 0 8px rgba(0, 0, 0, 0.15)'
  },
  header: {
    padding: '16px 20px',
    backgroundColor: '#1b1b1b',
    color: '#ffffff',
    display: 'flex' as const,
    alignItems: 'center' as const,
    justifyContent: 'space-between' as const
  },
  headerTitle: {
    fontSize: '16px',
    fontWeight: 600 as const,
    margin: 0
  },
  closeButton: {
    background: 'none',
    border: 'none',
    color: '#ffffff',
    fontSize: '18px',
    cursor: 'pointer',
    padding: '4px 8px'
  },
  content: {
    padding: '20px'
  },
  sectionTitle: {
    fontSize: '14px',
    fontWeight: 600 as const,
    margin: '16px 0 8px',
    color: theme.textColor
  },
  list: {
    margin: 0,
    paddingLeft: '16px',
    color: theme.grayTextColor,
    lineHeight: 1.6,
    fontSize: '13px'
  },
  loadingText: {
    color: theme.grayTextColor,
    fontSize: '13px',
    textAlign: 'center' as const,
    padding: '20px 0'
  },
  errorText: {
    color: '#dc3545',
    fontSize: '13px',
    textAlign: 'center' as const,
    padding: '20px 0'
  }
});

export default RulesPanel;
